/**
 * Lista embebida en una ficha.
 *
 * Deliberadamente **no** es la tabla del ERP. Dentro de una ficha se
 * enseñan las primeras filas y se enlaza al listado completo, por dos
 * razones: dos tablas en la misma página se pelearían por los mismos
 * parámetros de la URL, y nadie pagina dentro de una pestaña — se abre el
 * listado, que para eso tiene filtros y exportación.
 *
 * Si hay más filas de las que caben, **se dice cuántas**. Una lista
 * recortada en silencio es peor que no enseñar nada: quien la lea creerá
 * que eso es todo lo que hay.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';

export interface MiniColumn<Row> {
  label: string;
  render: (row: Row) => React.ReactNode;
  numeric?: boolean;
}

interface Props<Row> {
  /** Ruta del listado en la API, sin prefijo. */
  path: string;
  /** Filtros fijos de esta ficha: `{ customerId: '…' }`. */
  params: Record<string, string>;
  columns: ReadonlyArray<MiniColumn<Row>>;
  /** Enlace al listado completo, ya filtrado. */
  seeAll?: string;
  emptyMessage: string;
  limit?: number;
  onCount?: (total: number | null) => void;
}

interface Page<Row> {
  items: Row[];
  total: number | null;
}

export function MiniList<Row extends { id: string }>({
  path,
  params,
  columns,
  seeAll,
  emptyMessage,
  limit = 10,
  onCount,
}: Props<Row>): React.ReactNode {
  const { api } = useSession();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify(params);

  /**
   * El aviso del total, en una referencia y no en las dependencias.
   *
   * Estaba en el array de dependencias del efecto, y la ficha del cliente le
   * pasa una función NUEVA en cada renderizado. El resultado era un bucle:
   * carga → avisa del total → la ficha se renderiza → nueva función → carga
   * otra vez. Se veía como microrrecargas al abrir las pestañas de centros y
   * contratos, y por debajo era una petición detrás de otra sin parar.
   *
   * En una referencia siempre se llama a la última versión sin que su
   * identidad reinicie el efecto.
   */
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const query = new URLSearchParams(JSON.parse(key) as Record<string, string>);
    query.set('limit', String(limit));

    api
      .get<Page<Row>>(`${path}?${query.toString()}`)
      .then((page) => {
        if (cancelled) return;
        setRows(page.items ?? []);
        setTotal(page.total);
        onCountRef.current?.(page.total);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // Un 403 aquí es normal: alguien sin permiso de facturación abre
        // la ficha de un cliente. Se dice como falta de permiso, no como
        // error del sistema.
        setError(
          caught instanceof ApiError && caught.status === 403
            ? 'No tiene permiso para ver esta información.'
            : caught instanceof ApiError
              ? caught.message
              : 'No se han podido cargar los datos.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, path, key, limit]);

  if (loading) return <p className="table__loading">Cargando…</p>;

  if (error !== null) {
    return (
      <p className="screen__note" role="status">
        {error}
      </p>
    );
  }

  if (rows.length === 0) {
    return <p className="screen__note">{emptyMessage}</p>;
  }

  const hidden = total !== null ? total - rows.length : 0;

  return (
    <div className="mini">
      <div className="scroller">
        <table className="table__grid table__grid--compact">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.label} className={column.numeric ? 'table__num' : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                {columns.map((column) => (
                  <td key={column.label} className={column.numeric ? 'table__num' : undefined}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mini__foot">
        {/* Se dice lo que NO se está enseñando: una lista recortada en
            silencio hace creer que eso es todo lo que hay. */}
        {hidden > 0
          ? `Se muestran ${rows.length} de ${total}. `
          : `${rows.length} ${rows.length === 1 ? 'registro' : 'registros'}. `}
        {seeAll && <Link to={seeAll}>Ver el listado completo</Link>}
      </p>
    </div>
  );
}
